import { useState } from "react";
import { endOfWeek, subMonths } from "date-fns";
import { CalendarDays, Check, CircleX } from "lucide-react";
import { useListContext, useTranslate } from "ra-core";
import type { DateRange } from "react-day-picker";
import omit from "lodash/omit";
import pickBy from "lodash/pickBy";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import {
  boundsToRange,
  type DateBounds,
  formatDateRange,
  GTE,
  hasBounds,
  LTE,
  matchPreset,
  PRESETS,
  presetBounds,
  rangeToBounds,
} from "./dateRangePresets";

const label = "resources.zoom_meetings.filters.date";

/**
 * The meeting date filter: one button showing the chosen range, opening presets
 * beside a range calendar. Writes the same `meeting_start_time@gte` / `@lte`
 * keys the old preset buttons did, so saved URLs keep working.
 *
 * A preset fills the calendar and leaves it open, so the range can be adjusted
 * from there: after This month, clicking the 20th narrows it to the 1st-20th.
 */
export const DateRangeFilter = ({
  className,
  size,
}: {
  className?: string;
  size?: "sm" | "lg";
}) => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { filterValues, setFilters } = useListContext();
  const [open, setOpen] = useState(false);
  // A range with a start and no end yet, held here rather than written, since
  // a one-sided bound would filter the list mid-pick. With its default `min` of
  // 0, react-day-picker completes a range on the first click (from and to the
  // same day), so this only matters if that default changes.
  const [draft, setDraft] = useState<DateRange | undefined>();
  // Bumped by a preset so the calendar remounts on the preset's months. Days
  // picked in the calendar leave it where it is.
  const [presetPicks, setPresetPicks] = useState(0);

  const bounds: DateBounds = { gte: filterValues[GTE], lte: filterValues[LTE] };
  const active = hasBounds(bounds);
  const applied = boundsToRange(bounds);
  const preset = matchPreset(bounds);

  const write = (next: DateBounds) => {
    const rest = omit(filterValues, [GTE, LTE]);
    const keys = pickBy({ [GTE]: next.gte, [LTE]: next.lte });
    setFilters({ ...rest, ...keys });
    setDraft(undefined);
  };

  const onSelect = (range: DateRange | undefined) => {
    if (!range?.from) return write({});
    if (!range.to) return setDraft(range);
    write(rangeToBounds(range));
  };

  const text = formatDateRange(bounds, translate);

  // The calendar only draws a range with a start. "Older" has none, so it opens
  // on its end month with nothing selected, and the next click starts a range.
  const selected: DateRange | undefined =
    draft ??
    (applied.from ? { from: applied.from, to: applied.to } : undefined);
  const anchor = applied.to ?? applied.from ?? new Date();

  return (
    <div className="flex w-auto md:w-full items-center gap-1">
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setDraft(undefined);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant={active ? "highlight" : "ghost"}
            size={size}
            className={cn(
              "cursor-pointer flex-1 justify-start gap-2 px-2.5",
              className,
            )}
          >
            <CalendarDays className="opacity-60" />
            <span className="truncate">{text}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-auto max-w-[calc(100vw-2rem)] p-0"
        >
          <div className="flex flex-col md:flex-row">
            <div className="flex flex-row flex-wrap md:flex-col gap-1 p-2 md:w-36 md:border-r border-b md:border-b-0">
              {PRESETS.map((p) => (
                <Button
                  key={p}
                  variant={preset === p ? "secondary" : "ghost"}
                  size="sm"
                  className="cursor-pointer justify-between"
                  onClick={() => {
                    write(presetBounds(p));
                    setPresetPicks((n) => n + 1);
                  }}
                >
                  {translate(`${label}.${p}`)}
                  {preset === p && <Check className="opacity-50" />}
                </Button>
              ))}
              {active && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="cursor-pointer justify-start text-muted-foreground"
                  onClick={() => {
                    write({});
                    setOpen(false);
                  }}
                >
                  {translate(`${label}.clear`)}
                </Button>
              )}
            </div>
            <Calendar
              key={presetPicks}
              mode="range"
              selected={selected}
              onSelect={onSelect}
              numberOfMonths={isMobile ? 1 : 2}
              defaultMonth={isMobile ? anchor : subMonths(anchor, 1)}
              // No meeting starts in the future, but This week can end in next
              // month, and its last day should still be visible to adjust.
              endMonth={endOfWeek(new Date())}
            />
          </div>
        </PopoverContent>
      </Popover>
      {active && (
        <Button
          variant="ghost"
          size="icon"
          className="size-8 cursor-pointer shrink-0"
          aria-label={translate(`${label}.clear`)}
          onClick={() => write({})}
        >
          <CircleX className="opacity-50" />
        </Button>
      )}
    </div>
  );
};
